/**
 * Renderizador off-screen + trigger imperativo de download de PDF.
 *
 * Diferente da versão antiga: usa `useId()` para gerar um containerId único e
 * faz a query CSS escopada por esse id via `document.querySelector`. Isso
 * elimina o ref dance frágil que dependia de `localContainerRef.current` ser
 * atualizado em sincronia com flushSync.
 */
import { forwardRef, useId, useImperativeHandle, useMemo } from 'react'

import { PDFRenderer, PDFRendererProps } from './pdf-renderer'
import useGeneratePDF from '@/hooks/use-generate-pdf'

export interface PDFDownloadTriggerHandle {
  download: (options?: { filename?: string }) => Promise<void>
  isLoading: boolean
  progress: number
}

interface PDFDownloadTriggerProps extends Omit<PDFRendererProps, 'containerId'> {
  /** Render somente quando há conteúdo válido (evita custo de DOM). */
  active: boolean
}

export const PDFDownloadTrigger = forwardRef<PDFDownloadTriggerHandle, PDFDownloadTriggerProps>(function PDFDownloadTrigger(props, ref) {
  const { active, ...rest } = props
  const reactId = useId()
  const containerId = useMemo(() => `pdf-${reactId.replace(/:/g, '')}`, [reactId])
  const { generatePDF, isLoading, progress } = useGeneratePDF()

  useImperativeHandle(
    ref,
    () => ({
      download: async ({ filename }: { filename?: string } = {}) => {
        // Aguarda render + 2 RAFs + 200ms para garantir que o template
        // foi montado e que o useEffect do PDFRenderer rodou a medição.
        await new Promise(r => requestAnimationFrame(() => r(null)))
        await new Promise(r => requestAnimationFrame(() => r(null)))
        await new Promise(r => setTimeout(r, 200))
        await generatePDF({
          filename: filename || 'analise-nina.pdf',
          scale: 2,
          // Selector escopado por containerId único — robusto mesmo com múltiplas
          // instâncias de trigger na mesma página (caso da tabela).
          selector: `#${containerId} .pdf-page`
        })
      },
      isLoading,
      progress
    }),
    [generatePDF, isLoading, progress, containerId]
  )

  if (!active) return null

  return <PDFRenderer {...rest} containerId={containerId} />
})
